'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "style", "columnTypes"];
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { useRenderElement } from '@mui/x-scheduler-internals/base-ui-copy';
import { schedulerOccurrenceSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { useEventTimelinePremiumStoreContext } from "../../use-event-timeline-premium-store-context/index.mjs";
import { eventTimelinePremiumPresetSelectors } from "../../event-timeline-premium-selectors/index.mjs";
import { TimelineGridRootCssVars } from "./TimelineGridRootCssVars.mjs";
import { TimelineGridRootContext, DEFAULT_COLUMN_TYPES } from "./TimelineGridRootContext.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
export const TimelineGridRoot = /*#__PURE__*/React.forwardRef(function TimelineGridRoot(componentProps, forwardedRef) {
  const {
      // Rendering props

      columnTypes = DEFAULT_COLUMN_TYPES
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);
  if (process.env.NODE_ENV !== 'production') {
    const seen = new Set();
    for (const type of columnTypes) {
      if (seen.has(type)) {
        // TODO: fix mui/no-guarded-throw
        // eslint-disable-next-line mui/no-guarded-throw
        throw new Error(`MUI X Scheduler: The \`columnTypes\` prop of <TimelineGrid.Root /> contains a duplicate entry "${type}". ` + 'Arrow-key navigation relies on each column type appearing exactly once. ' + 'Remove the duplicate entry.');
      }
      seen.add(type);
    }
  }

  // Context hooks
  const store = useEventTimelinePremiumStoreContext();

  // Selector hooks
  const presetConfig = useStore(store, eventTimelinePremiumPresetSelectors.config);
  const resources = useStore(store, schedulerOccurrenceSelectors.groupedByResourceList, presetConfig.start, presetConfig.end);
  const rootRef = React.useRef(null);
  const [focusedCell, setFocusedCellState] = React.useState(null);
  const setFocusedCell = React.useCallback(coordinates => {
    setFocusedCellState(prev => prev?.columnType === coordinates.columnType && prev.rowIndex === coordinates.rowIndex ? prev : coordinates);
  }, []);
  const clearFocusedCellIfMatches = React.useCallback((columnType, rowIndex) => {
    setFocusedCellState(prev => prev?.columnType === columnType && prev.rowIndex === rowIndex ? null : prev);
  }, []);

  // Clears focusedCell when focus leaves the grid.
  const handleBlur = React.useCallback(event => {
    const nextTarget = event.relatedTarget;
    if (nextTarget) {
      if (!rootRef.current?.contains(nextTarget)) {
        setFocusedCellState(null);
      }
      return;
    }
    // `relatedTarget` is null with portals, programmatic blur, or window-blur.
    // Defer to let focus settle, then recheck whether focus truly left the grid.
    queueMicrotask(() => {
      if (!rootRef.current?.contains(document.activeElement)) {
        setFocusedCellState(null);
      }
    });
  }, []);
  const totalRowCount = resources.length;
  const contextValue = React.useMemo(() => ({
    focusedCell,
    setFocusedCell,
    clearFocusedCellIfMatches,
    columnTypes,
    totalRowCount
  }), [focusedCell, setFocusedCell, clearFocusedCellIfMatches, columnTypes, totalRowCount]);
  const element = useRenderElement('div', componentProps, {
    ref: [forwardedRef, rootRef],
    props: [elementProps, {
      role: 'grid',
      onBlur: handleBlur,
      style: {
        [TimelineGridRootCssVars.unitCount]: presetConfig.tickCount,
        [TimelineGridRootCssVars.rowCount]: resources.length
      }
    }]
  });
  return /*#__PURE__*/_jsx(TimelineGridRootContext.Provider, {
    value: contextValue,
    children: element
  });
});
if (process.env.NODE_ENV !== "production") TimelineGridRoot.displayName = "TimelineGridRoot";